import React, {FC, useMemo} from "react";
import { lightFormat } from 'date-fns';
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import {Document, Font, Image, Page, StyleSheet, Text, View} from "@react-pdf/renderer";
import {Shipment} from "../../../types/models/Shipment.ts";
import {Buyer} from "../../../types/models/Buyer.ts";
import {Location} from "../../../../location/types/models/Location.ts";
import {Provider} from "../../../types/models/Provider.ts";
import {WoodGroupsList} from "../../../types/models/MergeGroups.ts";
import {ShipmentStateDisplay} from "../../../types/models/ShipmentState.d";
import {textToBase64Barcode} from "../../../../utils/barcode.ts";
import {properties} from "../../../../properties";

Font.register({
    family: "Roboto",
    fonts: [
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf", fontWeight: 300},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf", fontWeight: 400},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf", fontWeight: 500},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf", fontWeight: 600},
    ],
})

const styles = StyleSheet.create({
    page: {
        fontFamily: "Roboto",
        backgroundColor: "#ffffff",
        color: "#424242",
        paddingTop: 30,
        paddingBottom: 30,
        paddingHorizontal: 40,
        boxSizing: "border-box",
    },
    owner: {
        fontSize: 10,
        marginBottom: 10,
        textAlign: "left",
    },
    details: {
        fontSize: 10,
        marginBottom: 10,
        textAlign: "left",
    },
    buyer: {
        fontSize: 10,
        fontWeight: "bold",
        marginBottom: 10,
        textAlign: "left",
        marginLeft: 30,
    },
    watermark: {
        position: "absolute",
        top: "50%",
        left: 0,
        right: 0,
        alignItems: "center",
        textTransform: 'uppercase',
        transform: "rotate(-45deg)",
        zIndex: -1,
        fontSize: 200,
        fontWeight: "bold",
        letterSpacing: 2,
        color: "rgba(150, 150, 150, 0.3)",
        userSelect: 'none',
    },
    footer: {
        position: 'absolute',
        top: 10,
        left: 40,
        right: 40,
        textAlign: 'center',
        fontSize: 7,
    },
})

const tableStyles = StyleSheet.create({
    table: {
        width: "100%",
        marginBottom: 20,
        borderRadius: 4,
        marginTop: 10,
    },
    rowBorderless: {
        flexDirection: "row",
        alignItems: "flex-start",
        minHeight: 10,
        flexWrap: "wrap",
    },
    twentyCell: {
        flex: 0.2,
    },
    thrtyCell: {
        flex: 0.3,
    },
    halfCell: {
        flex: 0.5,
    },
    eightyCell: {
        flex: 0.8,
    },
    row: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#e0e0e0",
        alignItems: "center",
        minHeight: 10,
        flexWrap: "wrap",
        ":not(:last-child)": {
            borderBottom: "none",
        },
    },
    rowBorder: {
        flexDirection: "row",
        alignItems: "center",
        minHeight: 10,
        flexWrap: "wrap",
    },
    rowHead: {
        flexDirection: "row",
    },
    cell: {
        flex: 1,
        padding: 3,
        fontSize: 8,
        color: "#424242",
        textTransform: "capitalize",
        alignItems: "center",
    },
    cellBorder: {
        flex: 1,
        padding: 3,
        fontSize: 8,
        borderBottomWidth: 1,
        borderBottomColor: "#e0e0e0",
        color: "#424242",
        textTransform: "capitalize",
        alignItems: "center",
    },
    cellRight: {
        flex: 1,
        padding: 3,
        fontSize: 8,
        color: "#424242",
        alignContent: "flex-end",
        textAlign: "right",
    },
    cellRightBold: {
        flex: 1,
        padding: 3,
        fontSize: 10,
        color: "#424242",
        alignContent: "flex-end",
        textAlign: "right",
        fontWeight: "bold",
    },
    headerCell: {
        padding: 3,
        fontSize: 10,
        backgroundColor: "#DAF7A6",
        fontWeight: "bold",
        overflow: "hidden",
        whiteSpace: "nowrap",
        alignItems: "center",
    },
    headerCellRight: {
        padding: 3,
        fontSize: 10,
        backgroundColor: "#DAF7A6",
        fontWeight: "bold",
        overflow: "hidden",
        whiteSpace: "nowrap",
        alignItems: "center",
        textAlign: "right",
    },
    totalCell: {
        backgroundColor: "lightgray",
    },
    innerHeading: {
        alignContent: "center",
        textAlign: "center",
        fontSize: 13,
        fontWeight: 700,
        marginBottom: 12,
        marginTop: 12,
    },
})

export interface IAccTableDataProps {
    sum: number,
    count: number,
    items: Array<ITableDataProps>,
}

export type ITableDataProps = {
    timberTypeName: string;
    className: string;
    thicknessClassName: string;
    totalVolume: number;
};

type IShipmentReportPdfProps = {
    shipment: Shipment;
    sourceLocation: Location | undefined;
    provider: Provider | undefined;
    buyer: Buyer | undefined;
    groupedData: WoodGroupsList;
    numberOfCopies: number;
};

const LogShipmentReportPdf: FC<IShipmentReportPdfProps> = 
    ({shipment, sourceLocation, provider, buyer, groupedData, numberOfCopies}) => {

    const barcodeData = useMemo(() => textToBase64Barcode(`${properties.webAppDomain}/dashboard/cuttings/${shipment.cuttingId}/shipments/${shipment.id}`), [shipment])
    const draft = shipment.state == ShipmentStateDisplay.InProgress || shipment.state == ShipmentStateDisplay.Upcoming

    return (
        <Document title={shipment.key}>
            { [...Array(numberOfCopies).keys()].map(i => i + 1).map((pagePosition) => (
            <Page size="A4" style={styles.page} key={pagePosition}>
                <View>

                    { draft &&
                        <View fixed style={styles.watermark}>
                            <Text>DRAFT</Text>
                        </View>
                    }

                    <View style={tableStyles.rowBorderless}>
                        <View style={[tableStyles.eightyCell]}>
                            <Text style={tableStyles.innerHeading}>Otpremnica {shipment.reportKey} ({shipment.key})</Text>

                            {provider &&
                                <View style={styles.owner}>
                                    <Text>{provider.name}</Text>
                                    <Text>{provider.addressIdAddress?.address ?? '-'}</Text>
                                    <Text>{provider.addressIdAddress?.city ?? '-'}</Text>
                                    <Text>PDV ID:{provider.vatId ?? '-'}</Text>
                                    <Text>IBAN: {provider.iban ?? '-'}</Text>
                                    <Text>{provider.bank ?? ''} SWIFT: {provider.swift ?? '-'}</Text>
                                </View>
                            }
                        </View>
                        <View style={tableStyles.twentyCell}>
                            <Image src={barcodeData} />
                        </View>
                    </View>

                    {buyer &&
                        <View>
                            <Text style={styles.details}>KUPAC:</Text>
                            <View style={styles.buyer}>
                                <Text>{buyer?.name ?? '-'}</Text>
                                <Text>{buyer?.addressIdAddress?.address ?? '-'}</Text>
                                <Text>{buyer?.addressIdAddress?.city ?? '-'}</Text>
                                <Text>PDV ID: {buyer?.vatId ?? '-'}</Text>
                            </View>
                        </View>
                    }
                    <View style={styles.details}>
                        <Text>Datum: {shipment.shipmentAt && lightFormat(shipment.shipmentAt, 'dd.MM.yyyy')}</Text>
                        <Text>Mjesto isporuke: {sourceLocation && sourceLocation.hierarchyName} {shipment.sourceParcel ? `> ${shipment.sourceParcel}` : ''}</Text>
                    </View>
                    <View wrap={true}>
                        <View style={tableStyles.table}>
                            <View style={[tableStyles.rowHead, {justifyContent: "space-between"}]}>
                                <Text key="Timber Type" style={[tableStyles.cell, tableStyles.headerCell]}>Razred kakvoče</Text>
                                <Text key="Class" style={[tableStyles.cell, tableStyles.headerCell]}>Vrsta drveta</Text>
                                <Text key="Thickness Clas" style={[tableStyles.cell, tableStyles.headerCell]}>Debljinski razred</Text>
                                <Text key="Sum Volume" style={[tableStyles.cellRight, tableStyles.headerCellRight]}>Drvni obujam</Text>
                                <Text key="Sum Volume Unit" style={[tableStyles.cellRight, tableStyles.headerCellRight]}>MJ</Text>
                            </View>
                            {groupedData.items
                            .sort((a, b) => a.cutting.name.localeCompare(b.cutting.name))
                            .map(cuttingRow =>
                                cuttingRow.items
                                .sort((a, b) => a.storage.name.localeCompare(b.storage.name))
                                .map(shipmentGroup => 
                                    shipmentGroup.items
                                        .sort((a, b) => a.timberType.name.localeCompare(b.timberType.name) || a.timberClass.name.localeCompare(b.timberClass.name))
                                        .map((groupedRow) => (
                                    <View key={`view_${groupedRow.timberType.id}_${groupedRow.timberClass.id}`}>
                                    {groupedRow.items
                                        .sort((a, b) => (a.thicknessClass.minDiameter || 0) - (b.thicknessClass.minDiameter || 0))
                                        .map((row) => (
                                        <View key={`view_${groupedRow.timberType.id}_${groupedRow.timberClass.id}_${row.thicknessClass.id}`}>
                                            <View style={tableStyles.row}>
                                                <Text style={tableStyles.cell}>{groupedRow.timberClass.reportNameShort || groupedRow.timberClass.name}</Text>
                                                <Text style={tableStyles.cell}>{groupedRow.timberType.reportNameShort || groupedRow.timberType.name}</Text>
                                                <Text style={tableStyles.cell}>{row.thicknessClass.reportNameShort || row.thicknessClass.name}</Text>
                                                <Text style={tableStyles.cellRight}>{fixedFormat(row.sum)}</Text>
                                                <Text style={tableStyles.cellRight}>m&#xB3;</Text>
                                            </View> 
                                        </View>
                                    ))}
                                    <View>
                                        <View style={tableStyles.row}>
                                            <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>&nbsp;</Text>
                                            <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>&nbsp;</Text>
                                            <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>Ukupno:</Text>
                                            <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>{fixedFormat(groupedRow.sum)}</Text>
                                            <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>m&#xB3;</Text>
                                        </View>
                                    </View>
                                </View>
                            ))))}
                            <View>
                                <View style={tableStyles.row}>
                                    <Text style={tableStyles.cell}></Text>
                                    <Text style={tableStyles.cell}></Text>
                                    <Text style={tableStyles.cellRightBold}>Ukupno:</Text>
                                    <Text style={tableStyles.cellRightBold}>{fixedFormat(groupedData.sum)}</Text>
                                    <Text style={tableStyles.cellRight}>m&#xB3;</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                    <View wrap={true}>
                        <View>
                            <View style={tableStyles.rowBorder}>
                                <Text style={tableStyles.cellRight}>Izdao:</Text>
                                <Text style={tableStyles.cellBorder}></Text>
                                <Text style={tableStyles.cellBorder}></Text>
                                <Text style={tableStyles.cellRight}>Preuzeo:</Text>
                                <Text style={tableStyles.cellBorder}></Text>
                                <Text style={tableStyles.cellBorder}></Text>
                            </View>

                        </View>
                    </View>
                </View>
                { numberOfCopies > 1 ?
                    <View fixed>
                        <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                            `Stranica ${pageNumber - (pagePosition - 1) * (totalPages / numberOfCopies)} / ${totalPages / numberOfCopies} | Kopija ${pagePosition} / ${numberOfCopies} | Otpremnica No: ${shipment.reportKey} (${shipment.key})`
                        )} />
                    </View>
                    :
                    <View fixed>
                        <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                            `Stranica ${pageNumber - (pagePosition - 1) * (totalPages / numberOfCopies)} / ${totalPages / numberOfCopies} | Otpremnica No: ${shipment.reportKey} (${shipment.key})`
                        )} />
                    </View>
                }
            </Page>)
            )}
        </Document>
    )
}

export default LogShipmentReportPdf
